import TextToChartData from "../code/TextToChartData";
import { selectNpsValues } from "../../store/chartOptions";
import { isSupportedAudioPitch } from "./audioPitch";

export const intervalNames = [
  "Root", "Minor 2nd", "Major 2nd", "Minor 3rd", "Major 3rd", "Perfect 4th",
  "Aug 4/ Dim 5th", "Perfect 5th", "Minor 6th", "Major 6th", "Minor 7th", "Major 7th",
];

const validTuning = tuning => Array.isArray(tuning) && tuning.length >= 1 &&
  tuning.length <= 20 && tuning.every(step => Number.isInteger(step) && step > 0 && step <= 127);

export function generateTrainerCharts(tuning, intervals, colors, layout) {
  if (!validTuning(tuning) || !Array.isArray(intervals) || intervals.length !== 12 ||
      !intervals.every(value => typeof value === "boolean") || !Array.isArray(colors) ||
      colors.length !== 12 || !selectNpsValues.includes(layout)) return [];
  return TextToChartData(tuning, intervals, colors, layout);
}

export const validChartIndex = (index, length) =>
  Number.isInteger(index) && index >= 0 && index < length ? index : 0;

export function buildTrainingModel(position, tuning, intervals, root, instrumentRoot, highRoot) {
  const model = { position: null, questions: [], rootNote: null, octave: 0, error: null };
  if (!validTuning(tuning) || !position || !Number.isInteger(position.notesOffset) ||
      !Number.isInteger(position.maxFret) || position.maxFret < 0 ||
      !Array.isArray(position.chartData) || position.chartData.length < 1 ||
      position.chartData.length > tuning.length + 1 ||
      !position.chartData.every(row => Array.isArray(row) && row.every(note =>
        Array.isArray(note) && Number.isInteger(note[0]) && note[0] >= 0))) {
    model.error = "No usable diagram is available. Choose an instrument, scale and layout.";
    return model;
  }

  model.position = position;
  let stringOffset = 0;
  position.chartData.forEach((row, string) => {
    if (string) stringOffset += tuning[string - 1];
    row.forEach(([fret]) => {
      const pitch = fret + position.notesOffset + stringOffset;
      model.questions.push({ pitch, interval: intervalNames[pitch % 12], string, fret });
    });
  });

  // The old first-string expression simplifies to this offset. It also works
  // when the lowest string is empty, as can happen with sparse interval sets.
  const delta = position.notesOffset + root - instrumentRoot;
  model.octave = delta >= 24 ? -24 : delta >= 12 ? -12 : delta >= 0 ? 0 :
    delta >= -12 ? 12 : delta >= -24 ? 24 : 0;
  const roots = model.questions.filter(note => note.pitch % 12 === 0).map(note => note.pitch);
  if (roots.length) model.rootNote = root + (highRoot ? Math.max(...roots) : Math.min(...roots)) + model.octave;

  if (!Array.isArray(intervals) || !intervals.some(Boolean)) {
    model.error = "Select some scale intervals, including the root, to start training.";
  } else if (!intervals[0]) {
    model.error = "Select the root interval to start training.";
  } else if (!Number.isInteger(root) || root < 0 || root > 127 ||
      !Number.isInteger(instrumentRoot) || instrumentRoot < 0 || instrumentRoot > 127 ||
      !isSupportedAudioPitch(model.rootNote)) {
    model.error = "This diagram has no playable root reference. Choose another diagram or instrument.";
  } else if (model.questions.some(note => !isSupportedAudioPitch(note.pitch + root + model.octave))) {
    model.error = "This diagram has notes outside the playable audio range. Choose another diagram or instrument.";
  } else if (new Set(model.questions.map(note => note.pitch)).size < 2) {
    model.error = "Choose a diagram with at least two different playable pitches for training.";
  }
  return model;
}

// Equivalent to rejection sampling from occurrences, but bounded even when no
// distinct pitch remains. Duplicate occurrences retain their existing weight.
export function chooseQuestion(questions, previousPitch, random = Math.random) {
  const candidates = questions.filter(note => note.pitch !== previousPitch);
  return candidates.length ? candidates[Math.floor(random() * candidates.length)] : null;
}

export const isCorrectPitch = (pitch, target, ignoreOctaves) =>
  Number.isInteger(pitch) && Number.isInteger(target) &&
  (ignoreOctaves ? pitch % 12 === target % 12 : pitch === target);
